import React from 'react';
import { useTranslation } from 'react-i18next';
import { partiallyMaskValue } from '@dxb-web-ui-framework/common/utils/DataObfuscation';
import { Box, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import { TableGridConfig } from '../../../../../content-composer/src/components/Generic/types';
import {
  formatAmountCellRenderer,
  stringWithPartiallyMaskValue,
  payrollDetailsStatusCellRenderer,
} from '../../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { useLanguage } from '../../../../../internationalization/src/contexts/Internationalization/hooks';

const useStyles = makeStyles(theme => ({
  typographyValue: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const usePayrollDetailsGridConfig = (): TableGridConfig => {
  const language = useLanguage();
  const locale = language.cultureName;
  const { t } = useTranslation();
  const classes = useStyles();

  return {
    gridOptions: {
      ariaTitle: t('usePayrollDetailsGridConfig.GridOptions.AriaTitle'),
      dataTestId: 'payroll-details-table',
      noResultsMsg: t('usePayrollDetailsGridConfig.NoResultMessage'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'ID',
        field: 'id',
        label: t('usePayrollDetailsGridConfig.Label.ID'),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Row Sn',
        field: 'rowSn',
        label: t('usePayrollDetailsGridConfig.Label.RowSn'),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Account credit',
        field: 'accountCredit',
        label: t('usePayrollDetailsGridConfig.Label.AccountCredit'),
        cellRenderer: stringWithPartiallyMaskValue(6, 18),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Amount',
        field: 'amount',
        label: t('usePayrollDetailsGridConfig.Label.Amount'),
        cellRenderer: formatAmountCellRenderer(locale, false),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Employee name',
        field: 'employeeName',
        label: t('usePayrollDetailsGridConfig.Label.EmployeeName'),
        columnHeaderCellProps: { align: 'right', colSpan: 1 },
        cellProps: { align: 'right', colSpan: 1 },
      },
      {
        id: 'Status',
        field: 'status',
        label: t('usePayrollDetailsGridConfig.Label.Status'),
        cellRenderer: payrollDetailsStatusCellRenderer,
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'employeeName',
          value: 'accountCredit',
          valueAreaLabel: 'accountCredit',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: any) => (
            <Typography
              color='textSecondary'
              component='p'
              aria-label={String(partiallyMaskValue(data.accountCredit, 6, 18))}
              className={classes.typographyValue}>
              {partiallyMaskValue(data.accountCredit, 6, 18)}
            </Typography>
          ),
        },
        {
          label: 'amount',
          value: 'amount',
          valueAreaLabel: 'status',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelAlignment: 'right',
          valueTransformer: (data: any) => (
            <Box mt={3} display='flex' component='span' justifyContent={'flex-end'}>
              {payrollDetailsStatusCellRenderer(data, { field: 'status' })}
            </Box>
          ),
        },
      ],
    },
  };
};

export { usePayrollDetailsGridConfig };
